import { Injectable } from "@angular/core";
import { Company } from "../../model/crm/company.model";
import { BrandingTypeEnum } from "../../model/forms/contract/form-template-contract";
import { FormGroupCompany } from "../../model/forms/form-group-company.model";
import { ThemeItemEnum } from "../../model/theme/contracts/theme-items.contract";
import { Theme } from "../../model/theme/theme.model";
import { BaseService } from "../base.service";
import { FONT_MAP } from "./contracts/theme-service.contract";

@Injectable({
  providedIn: "root",
})
/**
 * @description This service is used to manage theme-related operations.
 */
export class ThemeService extends BaseService {
  theme: Theme | null = null;
  ThemeItemEnum = ThemeItemEnum;
  showCFEPLogo = false;

  constructor() {
    super();
  }

  setTheme(brandingType: BrandingTypeEnum, company: Company | FormGroupCompany | undefined): void {
    this.theme = null;
    if (brandingType === BrandingTypeEnum.WhileLabelling && company) {
      this._applyFont(company.themeOnlineFont);
      this.theme = Object.assign(new Theme(), company);
    }

    if (
      brandingType === BrandingTypeEnum.CFEPOnly ||
      brandingType === BrandingTypeEnum.Cobranding
    ) {
      this.theme = Object.assign(new Theme(), {});
      this.theme.backgroundImageSignedUrl = "assets/images/CFEP-Background-survey.png";
    }

    this.showCFEPLogo =
      brandingType === BrandingTypeEnum.CFEPOnly || brandingType === BrandingTypeEnum.Cobranding;
  }

  getThemeColor(themeItem: ThemeItemEnum): string | null {
    switch (themeItem) {
      case ThemeItemEnum.startButton:
        return this.theme?.themeStartButtonColour ?? null;
      case ThemeItemEnum.nextButton:
        return this.theme?.themeNextButtonColour ?? null;
      case ThemeItemEnum.backButton:
        return this.theme?.themeBackButtonColour ?? null;
      case ThemeItemEnum.newSurveyButton:
        return this.theme?.themeNewSurveyButtonColour ?? null;
      case ThemeItemEnum.header:
        return this.theme?.themeHeaderColour ?? null;
      case ThemeItemEnum.headerTopBorder:
        return this.theme?.themeHeaderTopBorderColour ?? null;
      case ThemeItemEnum.sectionHeader:
        return this.theme?.themeSectionHeaderColour ?? null;
      case ThemeItemEnum.progressBar:
        return this.theme?.themeProgressBarColour ?? null;
      case ThemeItemEnum.restartButton:
        return this.theme?.themeNewSurveyButtonColour ?? null;
      case ThemeItemEnum.radioButton:
        return this.theme?.themeRadioButtonColour ?? null;
      case ThemeItemEnum.gradientStart:
        return this.theme?.themeGradientStartColour ?? null;
      case ThemeItemEnum.gradientEnd:
        return this.theme?.themeGradientEndColour ?? null;
      case ThemeItemEnum.scaleTrackColour:
        return this.theme?.themeScaleTrackColour ?? null;
      case ThemeItemEnum.scaleTrackSelectedColour:
        return this.theme?.themeScaleTrackSelectedColour ?? null;
      default:
        return null;
    }
  }

  getBackgroundThemeImage(): string | null {
    return this.theme?.backgroundImageSignedUrl ?? null;
  }

  private _applyFont(fontName?: string): void {
    const values = fontName ? FONT_MAP[fontName] ?? FONT_MAP["Frank New"] : FONT_MAP["Frank New"];
    const root = document.documentElement;
    root.style.setProperty("--font-regular", values.regular);
    root.style.setProperty("--font-bold", values.bold);
    root.style.setProperty("--font-light", values.light);
  }

  getContrastColour(hex: string | null): string {
    if (!hex || !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) {
      return "#000000";
    }
    const full = hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex;
    const r = parseInt(full.slice(1, 3), 16);
    const g = parseInt(full.slice(3, 5), 16);
    const b = parseInt(full.slice(5, 7), 16);
    // Perceived luminance (ITU-R BT.601)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "#000000" : "#ffffff";
  }
}
